import AddIcon from '@mui/icons-material/Add';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useContactpersonenVanBedrijf, zoekBedrijven, zoekContactpersonen } from '../api/klanten.ts';
import { zoekKandidaten } from '../api/kandidaten.ts';
import {
  certificaattypeLijst,
  functietitelLijst,
  talentpoolLijst,
  useLocatieVindenOfMaken,
  vaardigheidLijst,
  zoekLocaties,
} from '../api/referentie.ts';
import type {
  Bedrijf,
  Certificaattype,
  Contactpersoon,
  Functietitel,
  Kandidaat,
  Locatie,
  Talentpool,
  Vaardigheid,
  Vacature,
  Vacaturestatus,
} from '../api/typen.ts';
import { zoekVacatures } from '../api/vacatures.ts';
import { volledigeNaam } from '../helpers/formatteer.ts';
import { vacaturestatusLabels } from '../helpers/labels.ts';
import { useVerzenden } from '../hooks/useVerzenden.ts';
import { LijstKeuzeveld, ZoekKeuzeveld, ZoekMeerkeuzeveld } from './AsyncKeuzeveld.tsx';
import { FormulierDialoog } from './FormulierDialoog.tsx';
import { Tekstveld } from './velden.tsx';

interface Props {
  naam: string;
  label?: string | undefined;
  verplicht?: boolean | undefined;
  hulptekst?: string | undefined;
  uitgeschakeld?: boolean | undefined;
  autoFocus?: boolean | undefined;
}

/**
 * Een keuzeveld voor één functietitel.
 * @param props De naam in het formulier.
 * @returns Het veld.
 */
export function FunctietitelVeld(props: Props) {
  return (
    <ZoekKeuzeveld<Functietitel>
      {...props}
      label={props.label ?? 'Functietitel'}
      sleutel="functietitels"
      laad={(term) => functietitelLijst.zoek(term, 25)}
      optieLabel={(item) => item.naam}
      optieOndertitel={(item) => item.synoniemen.join(', ')}
    />
  );
}

/**
 * Een keuzeveld voor meerdere functietitels.
 * @param props De naam in het formulier.
 * @returns Het veld.
 */
export function FunctietitelsVeld(props: Props) {
  return (
    <ZoekMeerkeuzeveld<Functietitel>
      {...props}
      label={props.label ?? 'Functietitels'}
      sleutel="functietitels"
      laad={(term) => functietitelLijst.zoek(term, 25)}
      optieLabel={(item) => item.naam}
      optieOndertitel={(item) => item.synoniemen.join(', ')}
    />
  );
}

/**
 * Een keuzeveld voor meerdere talentpools.
 * @param props De naam in het formulier.
 * @returns Het veld.
 */
export function TalentpoolsVeld(props: Props) {
  return (
    <ZoekMeerkeuzeveld<Talentpool>
      {...props}
      label={props.label ?? 'Talentpools'}
      sleutel="talentpools"
      laad={(term) => talentpoolLijst.zoek(term, 25)}
      optieLabel={(item) => item.naam}
      optieOndertitel={(item) => item.omschrijving}
    />
  );
}

/**
 * Een keuzeveld voor één talentpool.
 * @param props De naam in het formulier.
 * @returns Het veld.
 */
export function TalentpoolVeld(props: Props) {
  return (
    <ZoekKeuzeveld<Talentpool>
      {...props}
      label={props.label ?? 'Talentpool'}
      sleutel="talentpools"
      laad={(term) => talentpoolLijst.zoek(term, 25)}
      optieLabel={(item) => item.naam}
    />
  );
}

/**
 * Een keuzeveld voor één vaardigheid.
 * @param props De naam in het formulier.
 * @returns Het veld.
 */
export function VaardigheidVeld(props: Props) {
  return (
    <ZoekKeuzeveld<Vaardigheid>
      {...props}
      label={props.label ?? 'Vaardigheid'}
      sleutel="vaardigheden"
      laad={(term) => vaardigheidLijst.zoek(term, 25)}
      optieLabel={(item) => item.naam}
      optieOndertitel={(item) => item.synoniemen.join(', ')}
    />
  );
}

/**
 * Een keuzeveld voor één certificaattype.
 * @param props De naam in het formulier.
 * @returns Het veld.
 */
export function CertificaattypeVeld(props: Props) {
  return (
    <ZoekKeuzeveld<Certificaattype>
      {...props}
      label={props.label ?? 'Certificaattype'}
      sleutel="certificaattypes"
      laad={(term) => certificaattypeLijst.zoek(term, 25)}
      optieLabel={(item) => item.naam}
      optieOndertitel={(item) => item.uitgever}
    />
  );
}

/**
 * Toont een locatie als plaats met postcode.
 * @param locatie De locatie.
 * @returns Bijvoorbeeld `Utrecht (3511 AB)`, of alleen `Utrecht` voor een locatie zonder postcode.
 */
export function locatieLabel(locatie: Locatie): string {
  return locatie.postcode ? `${locatie.plaats} (${locatie.postcode})` : locatie.plaats;
}

interface NieuweLocatieWaarden {
  postcode: string;
  plaats: string;
  provincie: string;
}

/**
 * Maakt een locatie aan op postcode, of geeft de bestaande locatie met die postcode terug.
 * @param props Wat er met de locatie gebeurt.
 * @returns De dialoog.
 */
export function NieuweLocatieDialoog({
  open,
  onSluit,
  onGemaakt,
}: {
  open: boolean;
  onSluit: () => void;
  onGemaakt: (locatie: Locatie) => void;
}) {
  const formulier = useForm<NieuweLocatieWaarden>({ defaultValues: { postcode: '', plaats: '', provincie: '' } });
  const vindOfMaak = useLocatieVindenOfMaken();
  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    async (waarden) => {
      const locatie = await vindOfMaak.mutateAsync({
        postcode: waarden.postcode,
        plaats: waarden.plaats,
        provincie: waarden.provincie || null,
      });
      onGemaakt(locatie);
      formulier.reset();
    },
    { melding: 'Locatie opgeslagen', naSucces: onSluit },
  );
  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel="Nieuwe locatie"
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        maxWidth="xs"
      >
        <Stack spacing={2}>
          <Tekstveld naam="postcode" label="Postcode" verplicht autoFocus placeholder="1234 AB" />
          <Tekstveld naam="plaats" label="Plaats" verplicht />
          <Tekstveld naam="provincie" label="Provincie" />
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}

/**
 * Een keuzeveld voor een locatie, met een knop om een locatie toe te voegen die nog niet bestaat.
 * @param props De naam in het formulier.
 * @returns Het veld.
 */
export function LocatieVeld(props: Props) {
  const [nieuw, setNieuw] = useState(false);
  const { setValue } = useFormContext();
  return (
    <>
      <ZoekKeuzeveld<Locatie>
        {...props}
        label={props.label ?? 'Locatie'}
        sleutel="locaties"
        laad={zoekLocaties}
        optieLabel={locatieLabel}
        optieOndertitel={(item) => item.provincie}
        hulptekst={props.hulptekst ?? 'Zoek op plaats of postcode'}
        extra={
          <Tooltip title="Nieuwe locatie">
            <IconButton size="small" onClick={() => setNieuw(true)} aria-label="Nieuwe locatie">
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />
      <NieuweLocatieDialoog
        open={nieuw}
        onSluit={() => setNieuw(false)}
        onGemaakt={(locatie) => setValue(props.naam, locatie, { shouldDirty: true })}
      />
    </>
  );
}

/**
 * Een keuzeveld voor een bedrijf.
 * @param props De naam in het formulier.
 * @returns Het veld.
 */
export function BedrijfVeld(props: Props) {
  return (
    <ZoekKeuzeveld<Bedrijf>
      {...props}
      label={props.label ?? 'Bedrijf'}
      sleutel="bedrijven"
      laad={zoekBedrijven}
      optieLabel={(item) => item.naam}
      optieOndertitel={(item) => item.sector}
    />
  );
}

/**
 * Een keuzeveld voor een contactpersoon van een bedrijf, of zonder bedrijf uit alle contactpersonen.
 * @param props De naam in het formulier en het bedrijf.
 * @returns Het veld.
 */
export function ContactpersoonVeld(props: Props & { bedrijfId?: string | null | undefined }) {
  const vanBedrijf = useContactpersonenVanBedrijf(props.bedrijfId);
  const { bedrijfId, ...rest } = props;
  if (bedrijfId) {
    return (
      <LijstKeuzeveld<Contactpersoon>
        {...rest}
        label={props.label ?? 'Contactpersoon'}
        opties={vanBedrijf.data ?? []}
        laden={vanBedrijf.isPending}
        optieLabel={volledigeNaam}
        optieOndertitel={(item) => item.functie}
      />
    );
  }
  return (
    <ZoekKeuzeveld<Contactpersoon>
      {...rest}
      label={props.label ?? 'Contactpersoon'}
      sleutel="contactpersonen"
      laad={zoekContactpersonen}
      optieLabel={volledigeNaam}
      optieOndertitel={(item) => item.functie}
    />
  );
}

/**
 * Een keuzeveld voor een kandidaat.
 * @param props De naam in het formulier.
 * @returns Het veld.
 */
export function KandidaatVeld(props: Props) {
  return (
    <ZoekKeuzeveld<Kandidaat>
      {...props}
      label={props.label ?? 'Kandidaat'}
      sleutel="kandidaten"
      laad={zoekKandidaten}
      optieLabel={volledigeNaam}
      optieOndertitel={(item) => item.email ?? item.telefoon}
    />
  );
}

/**
 * Een keuzeveld voor een vacature, standaard alleen open vacatures.
 * @param props De naam in het formulier en optioneel de status.
 * @returns Het veld.
 */
export function VacatureVeld(
  props: Props & { status?: Vacaturestatus | undefined; alleStatussen?: boolean | undefined },
) {
  const { status, alleStatussen, ...rest } = props;
  const filter = alleStatussen ? undefined : (status ?? 'open');
  return (
    <ZoekKeuzeveld<Vacature>
      {...rest}
      label={props.label ?? 'Vacature'}
      sleutel={`vacatures:${filter ?? 'alle'}`}
      laad={(term) => zoekVacatures(term, filter)}
      optieLabel={(item) => item.titel}
      optieOndertitel={(item) => vacaturestatusLabels[item.status]}
    />
  );
}
